/**
 * Écran unique d'une entreprise suspendue ou dont l'abonnement est terminé :
 * plus aucune page ni donnée n'est affichée (le serveur refuse aussi toutes les
 * lectures). Le message est ferme mais exact : il dit ce qui est réellement
 * coupé, et que les données sont conservées.
 *
 * Le patron (hors assistance) peut demander la reprise de l'abonnement
 * (facturation manuelle, traitée par MyEquip) ; c'est la seule action mise en
 * avant. Il garde aussi un lien discret « Récupérer mes données » (décision 12
 * du 19 octobre 2026) vers une archive à télécharger, sans consultation. Les
 * autres membres sont invités à contacter leur responsable. Un compte qui a
 * d'autres entreprises peut y passer d'ici (25/10/2026).
 */
import type { ReactNode } from 'react';
import { ArrowLeftRight, Ban, FileSignature, Lock, QrCode, Users } from 'lucide-react';
import { useAuth } from '../../core/auth.js';
import { dateLongue } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { DetailPage } from '../../components/templates/templates.js';
import { Section } from '../../components/ui/Section.js';
import { Banner } from '../../components/ui/Feedback.js';
import { Badge } from '../../components/ui/Badge.js';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';
import { ExternalShell } from '../../components/shell/ExternalShell.js';
import { CompanySection } from '../../components/CompanySwitcher.js';
import { DATA_RECOVERY_PATH, useResumeRequest } from './subscription-hooks.js';

export function SuspendedScreen(): ReactNode {
  const { me, logout } = useAuth();
  const resume = useResumeRequest();
  const owner = resume.enabled;
  const status = me?.subscription?.status;
  // Terminé, ou aucun abonnement rattaché : l'accès est fermé, pas seulement suspendu.
  const ended = status === 'termine' || status === 'sans_abonnement';
  const company = me?.company_name ?? 'votre entreprise';
  const recoveryUntil = status === 'termine' ? me?.subscription?.recoveryUntil ?? null : null;
  const recoveryOpen = recoveryUntil !== null && Date.parse(recoveryUntil) > Date.now();
  const facts: { icon: ReactNode; text: string }[] = [
    { icon: <Users size={20} />, text: 'Vos techniciens n’ont plus accès à l’application.' },
    { icon: <Lock size={20} />, text: 'Clients, plannings, interventions, factures et fiches CERFA sont inaccessibles.' },
    { icon: <QrCode size={20} />, text: 'Vos clients ne peuvent plus vous signaler de panne : le QR de leurs machines ne fonctionne plus.' },
    { icon: <FileSignature size={20} />, text: 'Vos devis ne peuvent plus être signés et plus rien n’est envoyé à vos clients.' },
    { icon: <Ban size={20} />, text: 'Plus rien ne peut être enregistré.' },
    ...(me?.subscription?.orphanCustomers ? [{
      icon: <ArrowLeftRight size={20} />,
      text: ended
        ? 'Un mois après la fin de votre abonnement, vos clients qui scannent un QR se verront proposer d’autres artisans MyEquip de votre secteur.'
        : 'Sans reprise, un mois après la fin de votre abonnement, vos clients se verront proposer d’autres artisans MyEquip de votre secteur.',
    }] : []),
  ];
  const canAsk = owner && !resume.pending;
  const title = status === 'sans_abonnement' ? t('abonnement.ferme.titre')
    : ended ? 'Abonnement terminé' : owner ? 'Votre compte est suspendu' : 'Accès suspendu';
  const description = status === 'sans_abonnement'
    ? t('abonnement.ferme.description', { entreprise: company })
    : ended
      ? `L’abonnement MyEquip de ${company} est terminé : l’accès est fermé pour toute l’équipe.`
      : `L’abonnement MyEquip de ${company} n’est plus réglé : l’accès est coupé pour toute l’équipe.`;

  // Déconnexion discrète par choix (décision du 11 octobre 2026) : dans la barre, seule la reprise est mise en avant.
  return (
    <ExternalShell onLogout={() => void logout()}>
      <DetailPage
        header={{
          kicker: 'Abonnement MyEquip',
          title,
          badge: <Badge tone="danger">{status === 'sans_abonnement' ? t('abonnement.ferme.badge') : ended ? 'Terminé' : 'Suspendu'}</Badge>,
          description,
        }}
        {...(canAsk ? {
          actionBar: {
            primary: {
              label: ended ? 'Rouvrir mon compte' : t('abonnement.reprendre'),
              onClick: resume.send,
              pending: resume.sending,
              disabled: resume.loading,
            },
          },
        } : {})}
      >
        {owner && resume.pending ? (
          <Banner tone="info" title="Demande de reprise envoyée">L’équipe MyEquip vous recontacte pour rétablir l’accès.</Banner>
        ) : null}
        {resume.error ? <Banner tone="danger">{resume.error.message}</Banner> : null}
        {owner ? null : <Banner tone="info">Contactez le responsable de {company} pour rétablir l’accès.</Banner>}
        <Section title="Ce qui est coupé" flush>
          <RecordList label="Ce qui est coupé">
            {facts.map((fact) => (
              <li key={fact.text}><RecordRow lead={<RecordIcon>{fact.icon}</RecordIcon>} title={fact.text} /></li>
            ))}
          </RecordList>
        </Section>
        {recoveryUntil ? (
          // Terminé : la récupération est ouverte 30 jours, puis les données peuvent être supprimées.
          <Banner
            tone={recoveryOpen ? 'warn' : 'info'}
            title={recoveryOpen
              ? t('abonnement.recuperation.jusquAu', { date: dateLongue(recoveryUntil) })
              : t('abonnement.recuperation.fermee')}
            {...(owner && recoveryOpen ? { action: { label: t('abonnement.recuperation.lien'), to: DATA_RECOVERY_PATH } } : {})}
          >
            {recoveryOpen ? t('abonnement.recuperation.avantSuppression') : t('abonnement.recuperation.fermeeDetail')}
          </Banner>
        ) : (
          <Banner
            tone="info"
            title="Vos données sont conservées"
            {...(owner ? { action: { label: t('abonnement.recuperation.lien'), to: DATA_RECOVERY_PATH } } : {})}
          >
            Tout redevient accessible dès la reprise de l’abonnement.
          </Banner>
        )}
        {/* Les autres entreprises du compte restent ouvertes (25/10/2026). */}
        <CompanySection />
      </DetailPage>
    </ExternalShell>
  );
}
